import { useEffect, useRef } from 'react';
import {
  StyledFlexContainer,
  StyledGenericModal,
  StyledGenericModalContainer,
  StyledGenericModalHeader,
  StyledTitle,
} from './styles';
import { pageViewEventHandler } from '@/analytics/events';
import { ConnectedXButton } from '@/components/uielements/X';
import { GenericFocusTrap } from '@/components/uielements/genericFocusTrap';
import { getMobileStyles, parseCustomUserAgent } from '@/utils/mobile';

interface Props {
  modalOpen: boolean;
  name: string;
  toggleModal?: (show: boolean) => void;
  header?: string | JSX.Element;
  maxWidth?: number;
  confirmMessage?: any;
  cancelMessage?: any;
  disableModalClose?: boolean;
  onClose?: () => void;
  title?: string;
  /**
   * Dialog semantics, all opt-in (TEAM-14142 AC6).
   *
   * Every one of these defaults to off because no modal built on *this* shell
   * carried dialog semantics before. (The newer Radix-backed
   * `components/uielements/modal` does set both at runtime via
   * `@radix-ui/react-dialog` — it just never spells them as literals, which is
   * why a grep for the attribute names finds only this declaration.) Turning
   * them on unconditionally here would change how assistive tech announces
   * every other modal on this shell, none of which has been designed or tested
   * for it. So callers that need the semantics ask for them, and the rest are
   * untouched.
   */
  role?: 'dialog' | 'alertdialog';
  ariaModal?: boolean;
  ariaLabelledBy?: string;
  ariaDescribedBy?: string;
  /**
   * Dismiss on Escape and on a scrim tap, independently of `toggleModal` /
   * `disableModalClose`.
   *
   * The shipped Escape handler below requires `toggleModal` AND
   * `!disableModalClose`, which ties keyboard dismissal to rendering an X
   * button. A modal can legitimately need one without the other: TEAM-14142's
   * disclosure has no X in the design, but AC6 still requires Escape and a
   * scrim tap to work — both behaving "exactly as 'Change Date/Time' does",
   * i.e. routed to a specific action rather than a generic close.
   */
  onDismiss?: () => void;
}

const GenericModal: React.FC<Props> = ({
  modalOpen,
  name,
  toggleModal,
  header,
  maxWidth,
  disableModalClose,
  onClose,
  children,
  title,
  role,
  ariaModal,
  ariaLabelledBy,
  ariaDescribedBy,
  onDismiss,
}) => {
  const isCOOMobile_iOS = parseCustomUserAgent()?.mobile_ios;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Bail while closed. This effect runs on every render, but the
    // `if (!modalOpen) return null` below is what stops the modal rendering — so
    // without this guard a caller passing `onDismiss` would leave a live
    // document-level Escape handler behind a modal that is not on screen, and
    // Escape anywhere on the page would fire its dismiss action.
    if (!modalOpen) {
      return;
    }

    const close = (evt: KeyboardEvent) => {
      if (evt.keyCode !== 27) {
        return;
      }

      if (onDismiss) {
        onDismiss();
        return;
      }

      if (toggleModal && !disableModalClose) {
        toggleModal(false);
        onClose && onClose();
      }
    };

    document.addEventListener('keydown', close);

    return () => {
      document.removeEventListener('keydown', close);
    };
  }, [modalOpen, toggleModal, disableModalClose, onClose, onDismiss]);

  useEffect(() => {
    if (modalOpen) {
      pageViewEventHandler(name);
    }
  }, [modalOpen, name]);

  /**
   * Move focus onto the dialog container when it opens (AC6).
   *
   * Done explicitly rather than left to the focus trap's `initialFocus`, because
   * `GenericFocusTrap` only arms itself once an IntersectionObserver callback sees
   * a tabbable child AND its `active` prop is true — and `GenericModal` never
   * passed `active`, so for every modal built on this shell the trap has never
   * armed and `initialFocus` was never consulted. Verified in a real browser: the
   * dialog opened with focus still on `body`. Arming the trap is opt-in below;
   * this effect is what makes the landing position deterministic either way, and
   * it is also what makes the behaviour assertable in jsdom, which has no
   * IntersectionObserver at all.
   */
  useEffect(() => {
    if (modalOpen && role) {
      containerRef.current?.focus();
    }
  }, [modalOpen, role]);

  if (!modalOpen) {
    return null;
  }

  return (
    <GenericFocusTrap
      // Opt-in, and only for callers declaring dialog semantics: `aria-modal="true"`
      // promises assistive tech that the rest of the page is inert, so focus has to
      // actually be contained. Passing `false` for everyone else is equivalent to
      // the `undefined` they got before, so no existing modal changes behaviour.
      active={Boolean(role)}
      focusTrapOptions={{
        clickOutsideDeactivates: !disableModalClose,
        // AC6: focus lands on the dialog container, not on the primary button —
        // otherwise the diner's first Tab is already past the comparison row
        // that explains what the button will do. Falls back to focus-trap's
        // default (first tabbable) for every caller that opts out.
        // `false` (not undefined) is focus-trap's "don't move focus" value; it is
        // the safe answer if the ref has not attached yet, since undefined would
        // silently fall back to focusing the first tabbable element.
        // `escapeDeactivates: false`: focus-trap defaults it to true, which would
        // let Escape switch the trap off (and return focus behind the dialog) on
        // modals that refuse to close — focus-trap-react only re-arms on an
        // `active` false→true change, which never happens while mounted. Modals
        // that do close on Escape unmount, which releases the trap anyway.
        ...(role
          ? { initialFocus: () => containerRef.current ?? false, escapeDeactivates: false }
          : {}),
      }}
    >
      <StyledGenericModal
        style={{ ...(isCOOMobile_iOS && getMobileStyles(['paddingTop'])) }}
        // A scrim tap is a click on this element itself; clicks bubbling up from
        // the card must not dismiss.
        onClick={
          onDismiss ? (event) => event.target === event.currentTarget && onDismiss() : undefined
        }
      >
        <StyledGenericModalContainer
          ref={containerRef}
          maxWidth={maxWidth}
          role={role}
          aria-modal={role ? ariaModal : undefined}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          // Only focusable when it is the declared initial focus target, so we
          // don't add a stray tab stop to the modals that opt out.
          tabIndex={role ? -1 : undefined}
        >
          <div>
            {title && <StyledTitle> {title}</StyledTitle>}

            {!title && header && (
              <StyledFlexContainer $header>
                <StyledGenericModalHeader tabIndex={0}>
                  {typeof header === 'string' ? <p>{header}</p> : <>{header}</>}
                </StyledGenericModalHeader>
              </StyledFlexContainer>
            )}

            {toggleModal && !disableModalClose && (
              <ConnectedXButton onClick={() => toggleModal(false)} name={name} />
            )}
          </div>

          {children}
        </StyledGenericModalContainer>
      </StyledGenericModal>
    </GenericFocusTrap>
  );
};

export default GenericModal;
